import { Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-core';
import { ContentRequest, ContentRequestState } from '@amzn/awscat-pattern-library-service-model';
import React, { useState } from 'react';

import constants from '../../common/constants';
import { getContentPath } from '../../common/ContentPaths';
import UserIdAutosuggest from '../common/UserIdAutosuggest';
import BrowserHistory from '../utils/BrowserHistory';

import '../../resources/styles/scss/components/content-request/content-request-assignment-table.scss';

interface Props {
  contentRequest: ContentRequest;
  updateContentRequest: (
    contentRequestId: string,
    state: ContentRequestState,
    assigneeId: string,
  ) => void;
}

const UpdateContentActionButtons = ({ contentRequest, updateContentRequest }: Props) => {
  const { contentId } = contentRequest;
  const [showAcceptModal, setShowAcceptModal] = useState<boolean>(false);
  const [showDeclineModal, setShowDeclineModal] = useState<boolean>(false);
  const [isDeclining, setIsDeclining] = useState<boolean>(false);
  const [isUpdatingLater, setIsUpdatingLater] = useState<boolean>(false);
  const [isUpdatingNow, setIsUpdatingNow] = useState<boolean>(false);
  const [newAssigneeId, setNewAssigneeId] = useState<string>('');
  const [isUpdatingPublication, setIsUpdatingPublication] = useState<boolean>(false);

  const handleAcceptAssignment = () => setShowAcceptModal(true);
  const handleDeclineAssignment = () => setShowDeclineModal(true);
  const onAcceptModalDismissClick = () => setShowAcceptModal(false);
  const onDeclineModalDismissClick = () => setShowDeclineModal(false);

  const handleUpdateLater = async () => {
    try {
      setIsUpdatingLater(true);
      const { requestId, assigneeId } = contentRequest;
      await updateContentRequest(requestId, ContentRequestState.ACCEPTED, assigneeId ?? '');
    } finally {
      setIsUpdatingLater(false);
      onAcceptModalDismissClick();
    }
  };

  const handleUpdateNow = async () => {
    try {
      setIsUpdatingNow(true);
      const { requestId, assigneeId } = contentRequest;
      await updateContentRequest(requestId, ContentRequestState.IN_PROGRESS, assigneeId ?? '');
      setIsUpdatingNow(false);
      onAcceptModalDismissClick();
      BrowserHistory.push(
        getContentPath({ contentId: contentId ?? '', mode: 'draft', role: 'author' }),
      );
    } catch (error) {
      setIsUpdatingNow(false);
      onAcceptModalDismissClick();
    }
  };

  const handleUpdatePublication = async () => {
    try {
      setIsUpdatingPublication(true);
      const { requestId, assigneeId } = contentRequest;
      await updateContentRequest(requestId, ContentRequestState.IN_PROGRESS, assigneeId ?? '');
      setIsUpdatingPublication(false);
      BrowserHistory.push(
        getContentPath({ contentId: contentId ?? '', mode: 'draft', role: 'author' }),
      );
    } catch (error) {
      setIsUpdatingPublication(false);
    }
  };

  const handleDecline = async () => {
    try {
      setIsDeclining(true);
      const { requestId } = contentRequest;
      const newState = newAssigneeId ? ContentRequestState.ASSIGNED : ContentRequestState.REQUESTED;
      await updateContentRequest(requestId, newState, newAssigneeId);
    } finally {
      setIsDeclining(false);
      onDeclineModalDismissClick();
    }
  };

  const handleAssigneeSelected = (
    selectedUserId: string,
    selectedUserName: string,
    validationError: any,
  ) => {
    if (!validationError) {
      setNewAssigneeId(selectedUserId);
    } else if (newAssigneeId) {
      setNewAssigneeId('');
    }
  };

  const acceptFooter = (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" onClick={onAcceptModalDismissClick}>
          Cancel
        </Button>
        <Button variant="normal" onClick={handleUpdateLater} loading={isUpdatingLater}>
          {constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_UPDATE_LATER}
        </Button>
        <Button variant="primary" loading={isUpdatingNow} onClick={handleUpdateNow}>
          {constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_UPDATE_NOW}
        </Button>
      </SpaceBetween>
    </Box>
  );

  const declineFooter = (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" onClick={onDeclineModalDismissClick}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleDecline} loading={isDeclining}>
          Decline
        </Button>
      </SpaceBetween>
    </Box>
  );

  // @ts-expect-error TS7030 - strict migration
  const renderActionButtons = () => {
    const { state } = contentRequest;
    if (state === ContentRequestState.ACCEPTED) {
      return (
        <Button variant="normal" onClick={handleUpdatePublication} loading={isUpdatingPublication}>
          {constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_UPDATE_PUBLICATION}
        </Button>
      );
    } else if (state === ContentRequestState.ASSIGNED) {
      return (
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="normal" onClick={handleAcceptAssignment}>
            Accept
          </Button>
          <Button variant="normal" onClick={handleDeclineAssignment}>
            Decline
          </Button>
        </SpaceBetween>
      );
    }
  };

  return (
    <>
      {renderActionButtons()}
      <Modal
        visible={showAcceptModal}
        header={constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_ACCEPT_UPDATE_HEADER}
        footer={acceptFooter}
        onDismiss={onAcceptModalDismissClick}
        closeAriaLabel="Close modal"
      >
        {constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_ACCEPT_UPDATE_BODY}
      </Modal>
      <Modal
        visible={showDeclineModal}
        header={constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_DECLINE_HEADER}
        footer={declineFooter}
        onDismiss={onDeclineModalDismissClick}
        closeAriaLabel="Close modal"
      >
        <div>
          <p>
            {constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_DECLINE_BODY}
            <i> - Optional</i>
          </p>
          <Box color="text-body-secondary">
            {constants.CONTENT_REQUEST_ASSIGNMENT_TABLE_DECLINE_BODY_DESC}
          </Box>
          <UserIdAutosuggest onUserSelected={handleAssigneeSelected} />
        </div>
      </Modal>
    </>
  );
};

export default UpdateContentActionButtons;
